import React, { ReactNode, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { TyreAlarms } from "../data/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import { Link } from "react-router-dom";
import Icon from "@cloudscape-design/components/icon";
import StatusIndicator from "@cloudscape-design/components/status-indicator";

interface CellProps {
  Alarm: string;
}
const AlarmCell: React.FC<CellProps> = ({ Alarm }) => {
  let statusType: "success" | "error" | "warning" | "info" = "info"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (Alarm === "0") {
    statusType = "success";
  } else if (Alarm === "10") {
    statusType = "error";
  } else if (Alarm === "20") {
    statusType = "warning";
  } else if (Alarm === "30") {
    statusType = "info";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Operational Normal"
        : statusType === "error"
        ? "Under Pressure"
        : statusType === "warning"
        ? "Overheated"
        : statusType === "info"
        ? "Low Battery"
        : "Unknown Status"}
    </StatusIndicator>
  );
};

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  TyreMaster: TyreAlarms[];
}

const columnDefinitions: TableProps<TyreAlarms>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: "5%",
  },
  {
    header: "S/N",
    cell: ({ sn }) => <Link to={`/vehicles/tyres/alarms/${sn}`}>{sn}</Link>,
    sortingField: "sn",
    width: 100,
  },
  {
    header: "Sensor ID",
    cell: ({ sensorId }) => sensorId,
    sortingField: "sensorId",
    width: 100,
  },
  {
    header: "Brand",
    cell: ({ brand }) => brand,
    sortingField: "brand",
    width: 100,
  },
  {
    header: "Type",
    cell: ({ typetyre }) => typetyre,
    sortingField: "typetyre",
    width: 100,
  },

  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => (
      <Link to={`/Vehicle-Detail/${VehicleCode}`}>{VehicleCode}</Link>
    ),
    sortingField: "VehicleCode",
    width: 100,
  },
  {
    header: "Position",
    cell: ({ tyreId }) => tyreId,
    sortingField: "tyreId",
    width: 100,
  },

  {
    header: "Alarm ",
    cell: AlarmCell,
    sortingField: "Alarm",
    width: 120,
  },
  {
    header: "Timestamps ",
    cell: ({ Timestamps }) => Timestamps,
    sortingField: "Timestamps",
    width: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly TyreAlarms[] = [],
  selectedItems: readonly TyreAlarms[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VehicleList({ TyreMaster }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<TyreAlarms>(TyreMaster, {
    filtering: {
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Table<TyreAlarms>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={false}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Tyre table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              TyreMaster,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Alarms
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Tyre'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        // selectionType="multi"
      />
    </>
  );
}
